import { $getSelection, LexicalEditor } from 'lexical';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { $createTextEntityNode } from '../../nodes/PhxTextEntityNode';
import useDraftManualGIQs from 'views/App/Portal/Policy/DraftManuals/_data_sources/giq/useDraftManualGIQs';
import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import { Button, Typography } from '@mui/material';
import { getDraftManual } from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import { getCustomTextEntities } from 'views/App/Portal/Policy/DraftManuals/_data_sources/customTextEntities/useCustomTextEntities';
import { AddCustomTextEntityModal } from './AddCustomTextEntityModal';
import { EditCustomTextEntityModal } from './EditCustomTextEntityModal';
import type { DraftManual } from 'views/App/Portal/Policy/_types/manuals/draft';
import type { CustomTextEntity } from 'views/App/Portal/Policy/_types/customTextEntities';

interface EntityData {
  id: string;
  label: string;
  description: string;
  value?: string;
}

interface TextEntity {
  key: string;
  data: EntityData;
  isCustom?: boolean;
  customEntityData?: CustomTextEntity;
}

export function InsertTextEntityDialog({
  activeEditor,
  onClose,
  manualId,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
  manualId: string;
}): JSX.Element {
  const giqQuery = useDraftManualGIQs(manualId);

  const [entities, setEntities] = useState<TextEntity[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [draftManual, setDraftManual] = useState<DraftManual | null>(null);
  const [isLoadingDraftManual, setIsLoadingDraftManual] = useState(false);
  const [customEntities, setCustomEntities] = useState<TextEntity[]>([]);
  const [isLoadingCustomEntities, setIsLoadingCustomEntities] = useState(false);
  const [isAddCustomModalOpen, setIsAddCustomModalOpen] = useState(false);
  const [isEditCustomModalOpen, setIsEditCustomModalOpen] = useState(false);
  const [selectedCustomEntity, setSelectedCustomEntity] = useState<CustomTextEntity | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const fetchDraftManual = async () => {
      if (!manualId) return;

      setIsLoadingDraftManual(true);
      try {
        const manual = await getDraftManual(manualId);
        setDraftManual(manual);

        // If manual level is AGENCY, fetch custom text entities
        if (manual.manualLevel === 'AGENCY') {
          setIsLoadingCustomEntities(true);
          try {
            const customTextEntitiesResponse = await getCustomTextEntities(manualId);
            const customEntitiesList = customTextEntitiesResponse.content.map((entity: CustomTextEntity) => ({
              key: entity.textEntity,
              data: {
                id: entity.id,
                label: entity.textEntity,
                description: entity.description,
                value: entity.substitutionValue,
              },
              isCustom: true,
              customEntityData: entity,
            }));
            setCustomEntities(customEntitiesList);
          } catch (error) {
            console.error('Failed to fetch custom text entities:', error);
          } finally {
            setIsLoadingCustomEntities(false);
          }
        } else {
          // Reset custom entities and loading state for non-AGENCY manuals
          setCustomEntities([]);
          setIsLoadingCustomEntities(false);
        }
      } catch (error) {
        console.error('Failed to fetch draft manual:', error);
      } finally {
        setIsLoadingDraftManual(false);
      }
    };

    fetchDraftManual();
  }, [manualId]);

  useEffect(() => {
    if (giqQuery.data?.questions) {
      // Extract unique text entities from questions
      const uniqueTextEntities = new Map<string, any>();

      giqQuery.data.questions.forEach((question: any) => {
        if (question.textEntity && question.textEntity.textEntity) {
          const textEntityKey = question.textEntity.textEntity;
          // Only add if not already present (ensures uniqueness)
          if (!uniqueTextEntities.has(textEntityKey)) {
            uniqueTextEntities.set(textEntityKey, {
              key: textEntityKey,
              data: {
                id: question.textEntity.id,
                label: question.textEntity.textEntity,
                description: question.textEntity.description,
                value: question.answer,
              },
            });
          }
        }
      });

      setEntities(Array.from(uniqueTextEntities.values()));
    }
  }, [giqQuery.data]);

  const filteredEntities = useMemo(() => {
    const value = searchValue.toLowerCase();
    const allEntities = [...entities, ...customEntities];
    return allEntities
      .filter(
        (entity) =>
          entity.data.label && typeof entity.data.label === 'string' && entity.data.label.toLowerCase().includes(value)
      )
      .sort((a, b) => a.data.label.localeCompare(b.data.label));
  }, [entities, customEntities, searchValue]);

  const debouncedSetSearchValue = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    debouncedSetSearchValue.current = debounce(setSearchValue, 300);

    return () => {
      if (debouncedSetSearchValue.current) {
        debouncedSetSearchValue.current.cancel();
      }
    };
  }, []);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (debouncedSetSearchValue.current) {
      debouncedSetSearchValue.current(value);
    }
  };

  const handleSearchClick = () => {
    const value = inputRef.current?.value || '';
    setSearchValue(value);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearchClick();
  };

  const onSubmit = (entity: TextEntity) => {
    activeEditor.update(() => {
      const selection = $getSelection();
      if (selection) {
        const textEntityNode = $createTextEntityNode(entity.data.label, {
          id: entity.data.id,
          description: entity.data.description,
        });
        selection.insertNodes([textEntityNode]);
      }
    });
    onClose();
  };

  const handleEditCustomEntity = (entity: TextEntity) => {
    if (entity.customEntityData) {
      setSelectedCustomEntity(entity.customEntityData);
      setIsEditCustomModalOpen(true);
    }
  };

  const handleAddCustomClick = () => {
    setIsAddCustomModalOpen(true);
  };

  const handleAddCustomSuccess = (newCustomEntity: CustomTextEntity) => {
    // Add the new custom entity to the list
    const newEntity: TextEntity = {
      key: newCustomEntity.textEntity,
      data: {
        id: newCustomEntity.id,
        label: newCustomEntity.textEntity,
        description: newCustomEntity.description,
        value: newCustomEntity.substitutionValue,
      },
      isCustom: true,
      customEntityData: newCustomEntity,
    };

    setCustomEntities((prev) => [...prev, newEntity]);
    setIsAddCustomModalOpen(false);
  };

  const handleEditCustomSuccess = (updatedCustomEntity: CustomTextEntity) => {
    // Update the custom entity in the list
    setCustomEntities((prev) =>
      prev.map((entity) =>
        entity.customEntityData?.id === updatedCustomEntity.id
          ? {
              ...entity,
              key: updatedCustomEntity.textEntity,
              data: {
                id: updatedCustomEntity.id,
                label: updatedCustomEntity.textEntity,
                description: updatedCustomEntity.description,
              },
              customEntityData: updatedCustomEntity,
            }
          : entity
      )
    );
    setIsEditCustomModalOpen(false);
    setSelectedCustomEntity(null);
  };

  return (
    <Box>
      <Box
        className='App'
        sx={{
          height: '50vh',
          marginInline: 'auto',
          display: 'grid',
          gridTemplateRows: 'auto 1fr',
          gap: 2,
          pt: 2,
        }}
      >
        <form
          onSubmit={handleFormSubmit}
          style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 8, alignItems: 'center' }}
        >
          <TextField
            id='search-bar'
            inputRef={inputRef}
            onInput={handleInput}
            variant='outlined'
            placeholder={t('contentEditor.textEntity.placeholder')}
            size='small'
            sx={{
              flex: 1,
              '&.MuiFormControl-root': {
                paddingTop: 0,
              },
            }}
          />
          <IconButton
            style={{ cursor: 'pointer' }}
            aria-label='search'
            onClick={handleSearchClick}
          >
            <SearchIcon style={{ fill: 'blue' }} />
          </IconButton>
          <Button
            variant='outlined'
            size='small'
            onClick={handleAddCustomClick}
            disabled={!draftManual || draftManual.manualLevel !== 'AGENCY'}
          >
            Add Custom
          </Button>
        </form>
        <Box sx={{ height: '100%', minHeight: 0 }}>
          {giqQuery.isLoading || isLoadingCustomEntities ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 4,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <GridSection
              columns={{
                label: {
                  header: t('contentEditor.textEntity.textEntity'),
                  flex: 2,
                  renderer: (params: any) => <Typography variant='body1'>{params.row.data.label}</Typography>,
                },
                value: {
                  header: t('contentEditor.textEntity.value'),
                  flex: 3,
                  renderer: (params: any) => (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                    >
                      {params.row.data.value || '--'}
                    </Typography>
                  ),
                },
                actions: {
                  header: '',
                  flex: 1,
                  renderer: (params: any) => (
                    <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end' }}>
                      {params.row.isCustom && (
                        <Button
                          variant='outlined'
                          size='small'
                          onClick={() => handleEditCustomEntity(params.row)}
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        variant='contained'
                        size='small'
                        onClick={() => onSubmit(params.row)}
                      >
                        {t('contentEditor.textEntity.insert')}
                      </Button>
                    </Box>
                  ),
                },
              }}
              data={filteredEntities}
              query={{
                isLoading: false,
                isError: giqQuery.isError,
                data: { pages: [{ count: filteredEntities.length }] },
              }}
              gridProps={{
                getRowId: (row: TextEntity) => row.data.id,
                rowHeight: 56,
                columnHeaderHeight: 40,
                hideFooterPagination: true,
              }}
            />
          )}
        </Box>
      </Box>

      {/* Add Custom Text Entity Modal */}
      <AddCustomTextEntityModal
        open={isAddCustomModalOpen}
        onClose={() => setIsAddCustomModalOpen(false)}
        manualId={manualId}
        onSuccess={handleAddCustomSuccess}
      />

      {/* Edit Custom Text Entity Modal */}
      {selectedCustomEntity && (
        <EditCustomTextEntityModal
          open={isEditCustomModalOpen}
          onClose={() => {
            setIsEditCustomModalOpen(false);
            setSelectedCustomEntity(null);
          }}
          manualId={manualId}
          textEntityId={selectedCustomEntity.id}
          currentEntity={selectedCustomEntity}
          onSuccess={handleEditCustomSuccess}
        />
      )}
    </Box>
  );
}
